import React, { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCallRecordingControllerGetCallRecordingQuery } from '@shared/services/apiService/apiService';

interface RecordingPreviewProps {
  callId: string;
  onClose: () => void;
}

export const RecordingPreview: React.FC<RecordingPreviewProps> = ({ callId, onClose }) => {
  const { t } = useTranslation();
  const [audioError, setAudioError] = useState(false);

  const { data, isLoading, isError } = useCallRecordingControllerGetCallRecordingQuery({
    id: callId,
  });

  useEffect(() => setAudioError(false), [callId]);

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="recording-preview-title">
      <DialogTitle id="recording-preview-title" sx={{ pr: 6 }}>
        {t('recordingPreview.title')}
        <IconButton
          onClick={onClose}
          aria-label={t('recordingPreview.close')}
          sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && (
          <Stack alignItems="center" spacing={2} py={2}>
            <CircularProgress size={28} />
            <Typography>{t('recordingPreview.loading')}</Typography>
          </Stack>
        )}
        {isError && <Alert severity="error">{t('recordingPreview.queryError')}</Alert>}
        {!isLoading && !isError && !audioError && data?.url && (
          <Box
            component="audio"
            controls
            preload="metadata"
            width="100%"
            src={data.url}
            onError={() => setAudioError(true)}>
            {t('recordingPreview.unsupported')}
          </Box>
        )}
        {audioError && <Alert severity="error">{t('recordingPreview.audioError')}</Alert>}
      </DialogContent>
    </Dialog>
  );
};
